import {isRejected} from '@reduxjs/toolkit'

import {
    ApiAsyncThunkFailError,
    ApiAsyncThunkAuthError,
    ApiAsyncThunkServerError,
    ApiAsyncThunkNetworkError,
    ApiAsyncThunkTimeoutError,
    ApiAsyncThunkMaintenanceError,
    ApiAsyncThunkUnexpectedError,
    ApiAsyncThunkAbortError,
} from './interfaces/error'

/**
 * apiAsyncThunkError 형태인지 확인하는 타입가드 생성기
 */
function createApiAsyncThunkError<E>({key}: {key: string}) {
    return (value: unknown): value is E => {
        return !!(
            value &&
            typeof value === 'object' &&
            'type' in value &&
            value['type'] === key &&
            'actionType' in value
        )
    }
}

/**
 * action 이 ApiAsyncThunkError 형태인지 확인하는 타입가드 생성기
 */
function createApiAsyncThunkRejected<E>({
    isApiAsyncThunkError,
}: {
    isApiAsyncThunkError: (value: unknown) => value is E
}) {
    return (
        action: unknown,
    ): action is ReturnType<typeof isRejected> & {
        type: string
        payload: E
    } => {
        return isRejected(action) && action.payload !== undefined && isApiAsyncThunkError(action.payload)
    }
}

/**
 * apiAsyncThunkAuthError 형태인지 확인하는 타입가드
 */
export const isApiAsyncThunkNetworkPayload = createApiAsyncThunkError<ApiAsyncThunkNetworkError>({
    key: 'API_ASYNC_THUNK_NETWORK_ERROR',
})

/**
 * action 이 ApiAsyncThunkNetWorkError 형태인지 확인하는 타입가드
 */
export const isApiAsyncThunkNetworkRejected = createApiAsyncThunkRejected({
    isApiAsyncThunkError: isApiAsyncThunkNetworkPayload,
})

/**
 * apiAsyncThunkAuthError 형태인지 확인하는 타입가드
 */
export const isApiAsyncThunkFailPayload = createApiAsyncThunkError<ApiAsyncThunkFailError>({
    key: 'API_ASYNC_THUNK_FAIL_ERROR',
})

/**
 * action 이 ApiAsyncThunkFailError 형태인지 확인하는 타입가드
 */
export const isApiAsyncThunkFailRejected = createApiAsyncThunkRejected({
    isApiAsyncThunkError: isApiAsyncThunkFailPayload,
})

/**
 * apiAsyncThunkAuthError 형태인지 확인하는 타입가드
 */
export const isApiAsyncThunkAuthError = createApiAsyncThunkError<ApiAsyncThunkAuthError>({
    key: 'API_ASYNC_THUNK_AUTH_ERROR',
})

/**
 * action 이 ApiAsyncThunkAuthError 형태인지 확인하는 타입가드
 */
export const isApiAsyncThunkAuthRejected = createApiAsyncThunkRejected({
    isApiAsyncThunkError: isApiAsyncThunkAuthError,
})

/**
 * payload 형태를 확인하는 타입가드
 */
export function isApiAsyncThunkServerPayload<T>(payload: unknown): payload is ApiAsyncThunkServerError<T> {
    return createApiAsyncThunkError<ApiAsyncThunkServerError<T>>({
        key: 'API_ASYNC_THUNK_SERVER_ERROR',
    })(payload)
}

/**
 * payload 에서 response.result 값을 뽑는 함수
 */
export function getServerResponseResult<T>(payload: unknown): Maybe<T> {
    if (isApiAsyncThunkServerPayload<T>(payload)) {
        return payload.value.response?.result ?? null
    }
    return null
}

/**
 * action 이 ApiAsyncThunkServerError 형태인지 확인하는 타입가드
 */
export function isApiAsyncThunkServerRejected<T>(action: unknown): action is ReturnType<typeof isRejected> & {
    type: string
    payload: ApiAsyncThunkServerError<T>
} {
    return isRejected(action) && isApiAsyncThunkServerPayload<T>(action.payload)
}

/**
 * payload 형태를 확인하는 타입가드
 */
export function isApiAsyncThunkTimeoutPayload<T>(payload: unknown): payload is ApiAsyncThunkTimeoutError<T> {
    return createApiAsyncThunkError<ApiAsyncThunkTimeoutError<T>>({
        key: 'API_ASYNC_THUNK_TIMEOUT_ERROR',
    })(payload)
}

/**
 * action 이 ApiAsyncThunkTimeoutError 형태인지 확인하는 타입가드
 */
export function isApiAsyncThunkTimeoutRejected<T>(action: unknown): action is ReturnType<typeof isRejected> & {
    type: string
    payload: ApiAsyncThunkTimeoutError<T>
} {
    return isRejected(action) && isApiAsyncThunkTimeoutPayload<T>(action.payload)
}

/**
 * payload 형태를 확인하는 타입가드
 */
export function isApiAsyncThunkMaintenancePayload<T>(payload: unknown): payload is ApiAsyncThunkMaintenanceError<T> {
    return createApiAsyncThunkError<ApiAsyncThunkMaintenanceError<T>>({
        key: 'API_ASYNC_THUNK_MAINTENANCE_ERROR',
    })(payload)
}

/**
 * action 이 ApiAsyncThunkMaintenanceError 형태인지 확인하는 타입가드
 */
export function isApiAsyncThunkMaintenanceRejected<T>(action: unknown): action is ReturnType<typeof isRejected> & {
    type: string
    payload: ApiAsyncThunkMaintenanceError<T>
} {
    return isRejected(action) && isApiAsyncThunkMaintenancePayload<T>(action.payload)
}

/**
 * payload 형태를 확인하는 타입가드
 */
export function isApiAsyncThunkUnexpectedPayload(payload: unknown): payload is ApiAsyncThunkUnexpectedError {
    return createApiAsyncThunkError<ApiAsyncThunkUnexpectedError>({
        key: 'API_ASYNC_THUNK_UNEXPECTED_ERROR',
    })(payload)
}

/**
 * action 이 ApiAsyncThunkUnexpectedError 형태인지 확인하는 타입가드
 */
export function isApiAsyncThunkUnexpectedRejected<T>(action: unknown): action is ReturnType<typeof isRejected> & {
    type: string
    payload: ApiAsyncThunkMaintenanceError<T>
} {
    return isRejected(action) && isApiAsyncThunkMaintenancePayload<T>(action.payload)
}

export const isApiAsyncThunkUnexpectedGatewayError = (payload: unknown): boolean => {
    try {
        return (
            isApiAsyncThunkTimeoutPayload(payload) ||
            isApiAsyncThunkNetworkPayload(payload) ||
            isApiAsyncThunkUnexpectedPayload(payload) ||
            (isApiAsyncThunkServerPayload(payload) && !payload.value.response?.data?.code)
        )
    } catch {
        return false
    }
}

/**
 * payload 형태를 확인하는 타입가드
 */
export function isApiAsyncThunkAbortPayload(payload: unknown): payload is ApiAsyncThunkAbortError {
    return createApiAsyncThunkError<ApiAsyncThunkAbortError>({
        key: 'API_ASYNC_THUNK_ABORT_ERROR',
    })(payload)
}

/**
 * action 이 ApiAsyncThunkAbortError 형태인지 확인하는 타입가드
 */
export function isApiAsyncThunkAbortRejected<T>(action: unknown): action is ReturnType<typeof isRejected> & {
    type: string
    payload: ApiAsyncThunkServerError<T>
} {
    return isRejected(action) && isApiAsyncThunkServerPayload<T>(action.payload)
}
